// src/components/partners/tabs/DirectoryTabShadcn.jsx
import React, { useState } from 'react';
import {
  Search,
  Globe,
  Building2,
  ArrowRight,
  Wifi,
  WifiOff,
  RefreshCw
} from 'lucide-react';

// shadcn components
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Alert, AlertDescription } from '../../ui/alert';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';

// Import shadcn components
import PartnersSearchBarShadcn from '../../ui/forms/PartnersSearchBar/PartnersSearchBarShadcn';
import PartnersBusinessCardShadcn from '../../ui/cards/PartnersBusinessCard/PartnersBusinessCardShadcn';

import { cn } from '../../../utils/cn';

const DirectoryTabShadcn = ({
  searchResults = [],
  searchLoading = false,
  actionLoading = null,
  isConnected = true,
  onSearch,
  onSendRequest,
  onClearSearch,
  className = ''
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [connectionSuccess, setConnectionSuccess] = useState('');
  const [connectionError, setConnectionError] = useState('');

  // Handle search submission
  const handleSearch = async (term) => {
    try {
      setConnectionError('');
      setConnectionSuccess('');

      if (!isConnected) {
        setConnectionError('You must be online to search for partners');
        return;
      }

      setSearchTerm(term);
      await onSearch(term);
      setSearchPerformed(true);
    } catch (error) {
      setConnectionError(error.message || 'Failed to search businesses');
    }
  };

  // Handle connection request with better error handling
  const handleSendRequest = async (businessId, note) => {
    try {
      setConnectionError('');
      setConnectionSuccess('');

      if (!isConnected) {
        setConnectionError('You must be online to send connection requests');
        return;
      }

      const business = searchResults.find(b => b.id === businessId);
      await onSendRequest(businessId, note);

      setConnectionSuccess(`Connection request sent to ${business?.name || 'business'}!`);

      // Clear success message after 3 seconds
      setTimeout(() => setConnectionSuccess(''), 3000);
    } catch (error) {
      setConnectionError(error.message || 'Failed to send connection request');
    }
  };

  // Handle search clearing
  const handleClearSearch = () => {
    setSearchTerm('');
    setSearchPerformed(false);
    if (onClearSearch) {
      onClearSearch();
    }
  };

  const clearMessages = () => {
    setConnectionSuccess('');
    setConnectionError('');
  };

  return (
    <div className={cn("space-y-6", className)}>
      {/* Connection Status Warning */}
      {!isConnected && (
        <Alert className="mb-6">
          <WifiOff className="h-4 w-4" />
          <AlertDescription>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">You're currently offline</p>
                <p className="text-sm">Connect to the internet to search for partner businesses.</p>
              </div>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Search Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <CardTitle className="flex items-center gap-2">
              <Search className="w-5 h-5" />
              Business Directory
            </CardTitle>
            {!isConnected && <WifiOff className="w-4 h-4 text-amber-500" />}
          </div>
        </CardHeader>
        <CardContent>
          <PartnersSearchBarShadcn
            value={searchTerm}
            onChange={setSearchTerm}
            onSearch={handleSearch}
            isLoading={searchLoading}
            disabled={!isConnected}
            placeholder={isConnected ? "Search by business name or service type..." : "Connect to internet to search..."}
          />

          {/* Clear search button */}
          {searchPerformed && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-muted-foreground text-sm">
                Search results for "{searchTerm}"
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearSearch}
                className="flex items-center gap-2"
              >
                <RefreshCw className="w-3 h-3" />
                Clear Search
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Success Messages */}
      {connectionSuccess && (
        <Alert className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">
          <AlertDescription className="flex items-center justify-between">
            <span>{connectionSuccess}</span>
            <Button variant="ghost" size="sm" onClick={clearMessages}>
              Dismiss
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Error Messages */}
      {connectionError && (
        <Alert variant="destructive">
          <AlertDescription className="flex items-center justify-between">
            <span>{connectionError}</span>
            <Button variant="ghost" size="sm" onClick={clearMessages}>
              Dismiss
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Search Results */}
      {searchPerformed && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Search Results
              </CardTitle>
              <Badge variant="secondary">
                {searchResults.length} business{searchResults.length !== 1 ? 'es' : ''} found
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {searchLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="flex items-center gap-3 text-muted-foreground">
                  <div className="w-6 h-6 border-2 border-border border-t-primary rounded-full animate-spin"></div>
                  <span>Searching for partner businesses...</span>
                </div>
              </div>
            ) : searchResults.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {searchResults.map((business) => (
                  <PartnersBusinessCardShadcn
                    key={business.id}
                    business={business}
                    onSendRequest={handleSendRequest}
                    isLoading={actionLoading === business.id}
                    isOffline={!isConnected}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">No businesses found</h3>
                <p className="text-muted-foreground mb-4">
                  No businesses match "{searchTerm}". Try different keywords or check spelling.
                </p>
                <Button variant="outline" onClick={handleClearSearch}>
                  Try Different Search
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Initial State */}
      {!searchPerformed && (
        <Card>
          <CardContent className="text-center py-12">
            <Globe className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">Discover Partner Businesses</h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              Use the search above to find and connect with partner businesses in your network.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DirectoryTabShadcn;